import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, MessageSquare, Send, X } from 'lucide-react'
import { Button, ErrorBanner, Modal } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import { turbineService, type Comment } from '@/services/turbineService'
import { authService } from '@/services/authService'

// TODO(i18n): yorum panelinin metinleri; strings.ts bu birimin sahibi değil.
const COMMENTS_LOAD_ERROR = 'Yorumlar yüklenemedi.'
const COMMENT_SEND_ERROR = 'Yorum gönderilemedi. Lütfen tekrar deneyin.'
const COMMENT_UPDATE_ERROR = 'Yorum güncellenemedi. Lütfen tekrar deneyin.'
const COMMENT_DELETE_ERROR = 'Yorum silinemedi. Lütfen tekrar deneyin.'
const USER_LOAD_WARNING = 'Kullanıcı bilgisi alınamadı; yorum düzenleme ve silme seçenekleri görünmeyebilir.'

// Comment Sidebar Component
const CommentSidebar: React.FC<{
  turbineId: number
  isOpen: boolean
  onClose: () => void
}> = ({ turbineId, isOpen, onClose }) => {
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [editingComment, setEditingComment] = useState<number | null>(null)
  const [editText, setEditText] = useState('')
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set())
  const { t } = useLanguage()
  // Hatalar bu panelin İÇİNDE gösterilir: panel kendi backdrop'unu çiziyor,
  // sayfa gövdesindeki bir banner backdrop'un altında kalıp görünmez olurdu.
  const [loadError, setLoadError] = useState<string | null>(null)   // liste
  const [actionError, setActionError] = useState<string | null>(null) // gönder/düzenle
  const [userWarning, setUserWarning] = useState<string | null>(null) // kullanıcı bilgisi
  // Silme onayı artık native confirm() değil; hata onay kutusunun İÇİNDE kalsın
  // ve işlem başarısızsa kutu KAPANMASIN diye ortak Modal kullanılıyor.
  const [pendingDelete, setPendingDelete] = useState<Comment | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Load comments and user data
  useEffect(() => {
    if (isOpen) {
      loadComments()
      loadUserData()
    }
  }, [isOpen, turbineId])

  const loadUserData = async () => {
    try {
      const userData = await authService.getCurrentUser()
      setUser(userData)
      // DİKKAT: authService.getCurrentUser() SENKRONdur ve throw ETMEZ -- bozuk ya da
      // eksik oturum verisinde JSON.parse hatasını kendi içinde yutup `null` döner.
      // Yani asıl sessiz durum aşağıdaki catch değil, bu `null` dönüşüdür: kullanıcı
      // bilgisi yoksa canEditComment her zaman false döner ve düzenle/sil butonları
      // hiçbir açıklama olmadan kaybolur.
      setUserWarning(userData ? null : USER_LOAD_WARNING)
    } catch (error) {
      logError('Turbine360.comments.loadUser', error)
      setUserWarning(USER_LOAD_WARNING)
    }
  }

  const loadComments = async () => {
    try {
      setLoading(true)
      const response = await turbineService.getComments(turbineId)
      if (response.success && response.data) {
        setComments(response.data)
        setLoadError(null)
      } else {
        // success:false BİR HATADIR; eskiden else'siz geçiliyor ve panel
        // "Henüz yorum yok" diyerek hatayı gizliyordu.
        console.error('❌ Comment list request failed:', response.message)
        setLoadError(responseErrorMessage(response, t, COMMENTS_LOAD_ERROR))
      }
    } catch (error) {
      logError('Turbine360.comments.load', error)
      // Mevcut yorumlar KORUNUR: hata "yorum yok" gibi görünmemeli.
      setLoadError(toUserMessage(error, t, COMMENTS_LOAD_ERROR))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitComment = async () => {
    if (!newComment.trim() || submitting) return

    try {
      setSubmitting(true)
      setActionError(null)
      const response = await turbineService.addComment(turbineId, newComment.trim())
      if (response.success && response.data) {
        // Add the new comment to the list immediately (optimistic update)
        setComments(prev => [response.data!, ...prev])
        setNewComment('')
      } else {
        // Eskiden gönderim sessizce başarısız oluyordu: metin kutuda kalıyor,
        // kullanıcı gönderildi mi anlamıyordu. Yazılan metin KORUNUR.
        console.error('❌ Add comment request failed:', response.message)
        setActionError(responseErrorMessage(response, t, COMMENT_SEND_ERROR))
      }
    } catch (error) {
      logError('Turbine360.comments.submit', error)
      setActionError(toUserMessage(error, t, COMMENT_SEND_ERROR))
      // Reload on error to ensure consistency
      await loadComments()
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    const createdDate = new Date(dateString)
    if (!isNaN(createdDate.getTime())) {
      // Format: "21 Aralık 2025 - 14:50"
      const formattedDate = createdDate.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
      const timeString = createdDate.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
      })
      return `${formattedDate} - ${timeString}`
    }
    return ''
  }

  // Check if user can edit/delete comment (1 hour limit or superadmin)
  const canEditComment = (comment: Comment) => {
    if (!user) return false

    // Superadmin can edit all comments
    if (user.role === 'superadmin') return true

    // User can edit their own comments within 1 hour
    if (comment.user_id === user.id) {
      const commentTime = new Date(comment.created_at).getTime()
      const now = new Date().getTime()
      const oneHour = 60 * 60 * 1000 // 1 hour in milliseconds
      return (now - commentTime) < oneHour
    }

    return false
  }

  // Handle comment edit
  const handleEditComment = async (commentId: number) => {
    if (!editText.trim()) return

    try {
      setActionError(null)
      const response = await turbineService.updateComment(turbineId, commentId, editText.trim())
      if (response.success) {
        // Update the comment in the list immediately (optimistic update)
        setComments(prev => prev.map(comment =>
          comment.id === commentId
            ? { ...comment, comment: editText.trim(), updated_at: new Date().toISOString() }
            : comment
        ))
        setEditingComment(null)
        setEditText('')
      } else {
        // Hata: düzenleme kutusu AÇIK kalır, yazılan metin kaybolmaz.
        console.error('❌ Update comment request failed:', response.message)
        setActionError(responseErrorMessage(response, t, COMMENT_UPDATE_ERROR))
      }
    } catch (error) {
      // Eskiden alert() ile çıkıyordu (panelin dışında, i18n dışı).
      logError('Turbine360.comments.update', error)
      setActionError(toUserMessage(error, t, COMMENT_UPDATE_ERROR))
      // Reload on error to ensure consistency
      await loadComments()
    }
  }

  // Handle comment delete -- onay kutusu ortak Modal ile; yıkıcı işlem hata
  // verdiyse kutu KAPANMAZ ve hata kutunun içinde görünür.
  const handleDeleteComment = async () => {
    const target = pendingDelete
    if (!target || deleting) return

    try {
      setDeleting(true)
      setDeleteError(null)
      const response = await turbineService.deleteComment(turbineId, target.id)
      if (response.success) {
        // Remove the comment from the list immediately (optimistic update)
        setComments(prev => prev.filter(comment => comment.id !== target.id))
        setPendingDelete(null)
      } else {
        console.error('❌ Delete comment request failed:', response.message)
        setDeleteError(responseErrorMessage(response, t, COMMENT_DELETE_ERROR))
      }
    } catch (error) {
      // Eskiden alert() ile çıkıyordu (panelin dışında, i18n dışı).
      logError('Turbine360.comments.delete', error)
      setDeleteError(toUserMessage(error, t, COMMENT_DELETE_ERROR))
      // Reload on error to ensure consistency
      await loadComments()
    } finally {
      setDeleting(false)
    }
  }

  // Toggle comment expansion
  const toggleCommentExpansion = (commentId: number) => {
    const newExpanded = new Set(expandedComments)
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId)
    } else {
      newExpanded.add(commentId)
    }
    setExpandedComments(newExpanded)
  }

  // Truncate comment text
  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[9999]"
            onClick={onClose}
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-[99999] flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
                Yorumlar
              </h3>
              <Button
                onClick={onClose}
                variant="outline"
                size="sm"
                className="p-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Panel içi hata/uyarı alanı: modal-benzeri bir drawer olduğu için
                hatalar KULLANICININ BAKTIĞI yüzeyde, backdrop'un üstünde durur. */}
            {(loadError || userWarning) && (
              <div className="px-4 pt-4 space-y-2">
                {loadError && (
                  <ErrorBanner
                    message={loadError}
                    onRetry={loadComments}
                    onDismiss={() => setLoadError(null)}
                  />
                )}
                {userWarning && (
                  <ErrorBanner
                    message={userWarning}
                    tone="warning"
                    onRetry={loadUserData}
                    onDismiss={() => setUserWarning(null)}
                  />
                )}
              </div>
            )}

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : comments.length > 0 ? (
                comments.map((comment) => {
                  const isExpanded = expandedComments.has(comment.id)
                  const isLongComment = comment.comment.length > 100
                  const canEdit = canEditComment(comment)
                  const isEditing = editingComment === comment.id

                  return (
                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-sm text-gray-900">
                            {comment.user_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(comment.created_at)}
                          </span>
                            {comment.updated_at && comment.updated_at !== comment.created_at && (
                              <span className="text-xs text-blue-500 italic">
                                (düzenlendi)
                              </span>
                            )}
                        </div>
                      </div>

                        {/* Edit/Delete buttons */}
                        {canEdit && !isEditing && (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => {
                                setEditingComment(comment.id)
                                setEditText(comment.comment)
                              }}
                              className="text-gray-400 hover:text-blue-500 transition-colors p-1"
                              title="Yorumu düzenle"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setDeleteError(null)
                                setPendingDelete(comment)
                              }}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                              title="Yorumu sil"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                    </div>
                        )}
                  </div>

                      {/* Comment content */}
                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                            maxLength={500}
                          />
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">
                              {editText.length}/500
                            </span>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditComment(comment.id)}
                                disabled={!editText.trim()}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                              >
                                Kaydet
                              </button>
                              <button
                                onClick={() => {
                                  setEditingComment(null)
                                  setEditText('')
                                }}
                                className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs"
                              >
                                İptal
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                            {isLongComment && !isExpanded
                              ? truncateText(comment.comment)
                              : comment.comment
                            }
                          </p>
                          {isLongComment && (
                            <button
                              onClick={() => toggleCommentExpansion(comment.id)}
                              className="text-xs text-blue-500 hover:text-blue-600 mt-1 font-medium"
                            >
                              {isExpanded ? 'Daha az göster' : 'Daha fazla göster'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Henüz yorum yok</p>
                  <p className="text-gray-300 text-xs mt-1">İlk yorumu siz yapın!</p>
                </div>
              )}
            </div>

            {/* Comment Input */}
            <div className="p-4 border-t">
              <div className="space-y-3">
                {/* Gönderme/düzenleme hatası: aksiyonun hemen yanında. */}
                <ErrorBanner
                  message={actionError}
                  onDismiss={() => setActionError(null)}
                />
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Yorumunuzu yazın..."
                  className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  maxLength={500}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {newComment.length}/500
                  </span>
                  <Button
                    onClick={handleSubmitComment}
                    disabled={!newComment.trim() || submitting}
                    className="bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
                    size="sm"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Gönder
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Silme onayı. Hata slotu modalın içinde; işlem hata verirse modal
              açık kalır ve kullanıcı ne olduğunu görür. */}
          <Modal
            isOpen={!!pendingDelete}
            onClose={() => {
              setPendingDelete(null)
              setDeleteError(null)
            }}
            title="Yorumu sil"
            size="sm"
            error={deleteError}
            onDismissError={() => setDeleteError(null)}
            isSubmitting={deleting}
            footer={
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPendingDelete(null)
                    setDeleteError(null)
                  }}
                  disabled={deleting}
                >
                  İptal
                </Button>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleDeleteComment}
                  disabled={deleting}
                >
                  {deleting ? 'Siliniyor...' : 'Sil'}
                </Button>
              </div>
            }
          >
            <p className="text-sm text-gray-700">
              Bu yorumu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
          </Modal>
        </>
      )}
    </AnimatePresence>
  )
}

export default CommentSidebar
